import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { leadTimeValidator } from './lead-time';
import { parseNumberInput } from './number-input';

type ValidatePriceLeadTimeParams = {
  leadTime: string;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

/**
 * Validates a per-price lead time entered in whole weeks. Returns the validated
 * number of weeks, or `undefined` if invalid (an issue is raised in that case).
 */
export function validatePriceLeadTime(
  state: WritableQuoteBuilderStateType,
  { leadTime, quoteLineUuid, quotePriceUuid }: ValidatePriceLeadTimeParams,
) {
  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'leadTimeWeeks',
  });

  const raiseIssue = (description: string) =>
    upsertIssue(state, invalidIssueId, {
      title: 'Invalid lead time',
      description,
      severity: 'error',
      type: 'invalid',
      field: 'leadTimeWeeks',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'leadTimeWeeks',
        }),
      },
    });

  const parsed = parseNumberInput({
    value: leadTime,
    requiredMessage: 'Lead time is required',
    invalidMessage: 'Lead time must be a number',
  });

  if (parsed.error) {
    raiseIssue(parsed.error);
    return;
  }

  const result = leadTimeValidator.safeParse(parsed.parsed);

  if (!result.success) {
    raiseIssue(z.flattenError(result.error).formErrors[0]);
    return;
  }

  deleteIssue(state, invalidIssueId);

  return result.data;
}
